import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { isHttpOrRelativeUrl } from '@/utils/urlValidation';
import { cn } from '@/utils/cn';
import { focusSurface } from '@/components/ui/focusStyles';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PREVIEW_MAX_PAGE_HEIGHT = 800;
const MAX_CANVAS_PIXELS = 8_000_000;
const DEFAULT_PAGE_ASPECT = 4 / 5;

interface PageGeometry {
  document: PDFDocumentProxy;
  aspects: Record<number, number>;
  lastAspect: number;
}

interface PdfPageViewerProps {
  url: string;
  filename?: string;
  thumbnailUrl?: string;
  aspectRatioHint?: number;
  /** Preview fits one page inside a post mock; document uses the available width and lets its parent scroll. */
  variant?: 'preview' | 'document';
  /** Fixed-ratio social mocks keep their existing contain-in-frame treatment. */
  fitToFrame?: boolean;
  className?: string;
}

/** One page at a time avoids the browser PDF toolbar and its nested scrollbars. */
export const PdfPageViewer = ({
  url,
  filename,
  thumbnailUrl,
  aspectRatioHint,
  variant = 'document',
  fitToFrame = false,
  className,
}: PdfPageViewerProps) => {
  const { t } = useTranslation('common');
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [renderedPage, setRenderedPage] = useState<number | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [renderSize, setRenderSize] = useState({ width: 0, height: 0 });
  const [pageGeometry, setPageGeometry] = useState<PageGeometry | null>(null);
  const [thumbnailAspect, setThumbnailAspect] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    const measure = () => {
      const next = { width: element.clientWidth, height: element.clientHeight };
      setSize((current) => current.width === next.width && current.height === next.height ? current : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setThumbnailAspect(null);
    if (!thumbnailUrl) return;
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth && image.naturalHeight) setThumbnailAspect(image.naturalWidth / image.naturalHeight);
    };
    image.src = thumbnailUrl;
    return () => { image.onload = null; };
  }, [thumbnailUrl]);

  useEffect(() => {
    setDocument(null);
    setPage(1);
    setRenderedPage(null);
    setPageGeometry(null);
    setFailed(false);
    setLoading(true);
    if (!isHttpOrRelativeUrl(url)) {
      setLoading(false);
      setFailed(true);
      return;
    }

    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    const task = pdfjs.getDocument({ url });
    void task.promise.then(async (pdf) => {
      loaded = pdf;
      if (cancelled) {
        void pdf.destroy();
        return;
      }
      // Reserve the actual first-page shape before the expensive canvas render starts.
      const firstPage = await pdf.getPage(1);
      if (cancelled) return;
      const natural = firstPage.getViewport({ scale: 1 });
      const aspect = natural.width / natural.height;
      setPageGeometry({ document: pdf, aspects: { 1: aspect }, lastAspect: aspect });
      setDocument(pdf);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setFailed(true);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      if (loaded) void loaded.destroy();
      else void task.destroy();
    };
  }, [url]);

  const frameHeight = fitToFrame ? size.height : 0;

  useEffect(() => {
    if (!size.width || (fitToFrame && !frameHeight)) return;
    const next = { width: size.width, height: frameHeight };
    if (!renderSize.width) {
      setRenderSize(next);
      return;
    }
    if (Math.abs(next.width - renderSize.width) < 2 && Math.abs(next.height - renderSize.height) < 2) return;
    const timer = window.setTimeout(() => setRenderSize(next), 100);
    return () => window.clearTimeout(timer);
  }, [size.width, frameHeight, fitToFrame, renderSize.width, renderSize.height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!document || !canvas || renderSize.width === 0 || (fitToFrame && renderSize.height === 0)) return;
    let cancelled = false;
    let renderTask: ReturnType<PDFPageProxy['render']> | null = null;
    let buffer: HTMLCanvasElement | null = null;
    const releaseBuffer = () => {
      if (!buffer) return;
      buffer.width = 0;
      buffer.height = 0;
      buffer = null;
    };
    setFailed(false);

    void document.getPage(page).then((pdfPage) => {
      if (cancelled) return;
      const natural = pdfPage.getViewport({ scale: 1 });
      const aspect = natural.width / natural.height;
      setPageGeometry((current) => current?.document === document
        ? { document, aspects: { ...current.aspects, [page]: aspect }, lastAspect: aspect }
        : { document, aspects: { [page]: aspect }, lastAspect: aspect });
      const scale = fitToFrame
        ? Math.min(renderSize.width / natural.width, renderSize.height / natural.height)
        : variant === 'preview'
          ? Math.min(renderSize.width / natural.width, PREVIEW_MAX_PAGE_HEIGHT / natural.height)
          : renderSize.width / natural.width;
      const displayWidth = natural.width * scale;
      const displayHeight = natural.height * scale;
      const pixelRatio = Math.min(
        Math.max(1, window.devicePixelRatio || 1),
        2,
        Math.sqrt(MAX_CANVAS_PIXELS / (displayWidth * displayHeight))
      );
      const viewport = pdfPage.getViewport({ scale: scale * pixelRatio });
      const scratch = window.document.createElement('canvas');
      buffer = scratch;
      scratch.width = Math.max(1, Math.round(viewport.width));
      scratch.height = Math.max(1, Math.round(viewport.height));
      const context = scratch.getContext('2d');
      if (!context) {
        releaseBuffer();
        setFailed(true);
        return;
      }
      renderTask = pdfPage.render({ canvas: scratch, canvasContext: context, viewport });
      void renderTask.promise.then(() => {
        if (cancelled) return;
        const visibleContext = canvas.getContext('2d');
        if (!visibleContext) {
          releaseBuffer();
          setFailed(true);
          return;
        }
        canvas.width = scratch.width;
        canvas.height = scratch.height;
        canvas.style.width = `${Math.round(displayWidth)}px`;
        canvas.style.height = 'auto';
        visibleContext.drawImage(scratch, 0, 0);
        releaseBuffer();
        setRenderedPage(page);
      }).catch(() => {
        releaseBuffer();
        if (!cancelled) {
          setFailed(true);
        }
      });
    }).catch(() => {
      if (!cancelled) {
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
      renderTask?.cancel();
      releaseBuffer();
    };
  }, [document, page, renderSize.width, renderSize.height, variant, fitToFrame]);

  const pageCount = document?.numPages ?? 0;
  const showNavigation = variant === 'preview' || pageCount > 1;
  const activeGeometry = pageGeometry?.document === document ? pageGeometry : null;
  const candidateAspect = activeGeometry?.aspects[page]
    ?? activeGeometry?.lastAspect
    ?? (page === 1 ? thumbnailAspect ?? aspectRatioHint : null)
    ?? DEFAULT_PAGE_ASPECT;
  const pageAspect = Number.isFinite(candidateAspect) && candidateAspect > 0
    ? candidateAspect
    : DEFAULT_PAGE_ASPECT;
  const reservedHeight = variant === 'preview' && !fitToFrame && size.width > 0
    ? Math.min(size.width / pageAspect, PREVIEW_MAX_PAGE_HEIGHT)
    : undefined;
  const buttonClass = cn(
    'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-9 sm:w-9',
    focusSurface
  );
  const navigation = showNavigation && (
    <div className={cn(
      'flex shrink-0 items-center justify-center gap-3 bg-white px-2 py-1.5',
      variant === 'document' && 'sticky top-0 z-10 border-b border-gray-200'
    )}>
      <button
        type="button"
        onClick={() => setPage((value) => Math.max(1, value - 1))}
        disabled={page === 1 || pageCount === 0}
        className={buttonClass}
        aria-label={t('pdf_previous_page')}
      >
        <Icon name={ICONS.ANGLE_LEFT} size="sm" ariaHidden />
      </button>
      <span className="min-w-16 text-center text-xs font-medium tabular-nums text-gray-700" aria-live="polite">
        {pageCount ? t('pdf_page_of', { page, total: pageCount }) : t('loading')}
      </span>
      <button
        type="button"
        onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
        disabled={pageCount === 0 || page === pageCount}
        className={buttonClass}
        aria-label={t('pdf_next_page')}
      >
        <Icon name={ICONS.ANGLE_RIGHT} size="sm" ariaHidden />
      </button>
    </div>
  );

  return (
    <div className={cn('flex min-w-0 flex-col bg-gray-100', variant === 'preview' && (fitToFrame ? 'min-h-0 flex-1' : 'w-full'), className)}>
      {variant === 'document' && navigation}
      <div
        ref={viewportRef}
        className={cn(
          'relative flex w-full min-w-0 items-center justify-center overflow-hidden',
          variant === 'preview' && (fitToFrame ? 'min-h-0 flex-1' : 'min-h-48')
        )}
        style={reservedHeight === undefined ? undefined : { height: `${Math.round(reservedHeight)}px` }}
      >
        {renderedPage === null && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            {page === 1 && thumbnailUrl ? (
              <img src={thumbnailUrl} alt="" className="max-h-full max-w-full object-contain" />
            ) : (
              <Icon name={ICONS.FILE} size="xl" color="text-gray-400" ariaHidden />
            )}
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={cn('mx-auto block max-w-full bg-white shadow-sm', renderedPage === null && 'invisible')}
          role="img"
          aria-label={filename
            ? `${filename} · ${t('pdf_page_of', { page: renderedPage ?? page, total: pageCount || 1 })}`
            : t('pdf_page_of', { page: renderedPage ?? page, total: pageCount || 1 })}
        />
        {(loading || renderedPage !== page || failed) && (
          <div className="absolute inset-x-2 bottom-2 flex justify-center">
            <span className="rounded bg-white/90 px-2 py-1 text-xs text-gray-700 shadow-sm">
              {failed ? t('pdf_preview_unavailable') : t('loading')}
            </span>
          </div>
        )}
      </div>
      {variant === 'preview' && navigation}
      {failed && isHttpOrRelativeUrl(url) && (
        <a href={url} target="_blank" rel="noopener noreferrer" className={cn('self-center rounded px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50', focusSurface)}>
          {t('open_original')}
        </a>
      )}
    </div>
  );
};
